(() => {
  'use strict';
  if (window.__touteraTracking || window.Shopify?.designMode) return;
  const element = document.getElementById('toutera-tracking-config');
  if (!element) return;
  let config;
  try { config = JSON.parse(element.textContent); } catch { return; }
  if (config.enabled !== true || !config.firebase || !config.writerUrl || location.pathname === '/pages/customer-tracker') return;
  let writerPromise, writer;
  function loadWriter() {
    if (!writerPromise) writerPromise = import(config.writerUrl).then(module => module.createWriter(config.firebase,allowed)).then(value => writer=value).catch(error => { writerPromise=null; throw error; });
    return writerPromise;
  }
  window.__touteraTracking = true;
  const nativeFetch = window.fetch.bind(window);
  let visitor = null, session = null, lastCart = null, timer, activeFlush = null, pending = null, started = false;
  const queue = [];
  const allowed = () => window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
  const id = () => crypto.randomUUID();
  function identities() {
    if (!allowed()) return false;
    try {
      visitor = localStorage.getItem('toutera_visitor') || id();
      localStorage.setItem('toutera_visitor', visitor);
      session = sessionStorage.getItem('toutera_session') || id();
      sessionStorage.setItem('toutera_session', session);
    } catch { visitor ||= id(); session ||= id(); }
    return true;
  }
  const pendingKey = 'toutera_pending_activity_v2';
  function persist() {
    try {
      if (allowed() && queue.length) sessionStorage.setItem(pendingKey, JSON.stringify(queue.slice(-100)));
      else sessionStorage.removeItem(pendingKey);
    } catch {} // Storage restrictions must never block shopping.
  }
  function restore() {
    try {
      const saved = JSON.parse(sessionStorage.getItem(pendingKey) || '[]');
      if (Array.isArray(saved)) for (const item of saved.slice(-100)) {
        if (item && typeof item.type === 'string' && typeof item.path === 'string'
          && item.data && Number.isFinite(item.at) && item.at > Date.now()-23*60*60*1000) {
          // A previous page may have committed before unloading but missed its ACK.
          // A fresh ID permits replay under immutable-write rules (at-least-once).
          queue.push({...item, id:id()});
        }
      }
    } catch {}
    persist();
  }
  function event(type, data = {}) {
    if (!allowed() || !identities()) return;
    queue.push({id:id(), type, at:Date.now(), path:location.pathname, data});
    if (queue.length > 100) queue.splice(0,queue.length-100);
    persist();
    void flush();
  }
  function flush() {
    if (!allowed()) { queue.length = 0; persist(); return Promise.resolve(); }
    if (activeFlush) return activeFlush;
    if (!queue.length) return Promise.resolve();
    activeFlush = (async () => {
      try {
        const currentWriter = await loadWriter();
        while (queue.length && allowed()) {
          const batch = queue.slice(0,30);
          await currentWriter.send(batch,session,allowed, eventId => {
            const index = queue.findIndex(item=>item.id===eventId);
            if (index >= 0) queue.splice(index,1);
            persist();
          });
          if (!allowed()) break;
          // Compatibility with a cached older writer: successful full batch ACK.
          for (const item of batch) {
            const index=queue.findIndex(value=>value.id===item.id);
            if(index>=0)queue.splice(index,1);
          }
          persist();
        }
      } catch { persist(); } // Retry on next page/online/timer; keep in-flight records.
      finally { activeFlush=null; }
    })();
    return activeFlush;
  }
  function cartLines(cart) {
    return (cart.items || []).map(item => ({
      key:item.key, product_id:String(item.product_id), variant_id:String(item.variant_id),
      title:item.product_title || item.title, variant:item.variant_title || '',
      quantity:item.quantity, price:item.final_price / 100, url:item.url?.split('?')[0] || ''
    }));
  }
  function snapshot() {
    if (!allowed()) return Promise.resolve();
    if (pending) return pending;
    pending = (async () => {
    try {
      const root = config.root.endsWith('/') ? config.root : config.root + '/';
      const response = await nativeFetch(root + 'cart.js', {credentials:'same-origin',cache:'no-store'});
      if (!response.ok) return;
      const cart = await response.json();
      if (!allowed()) return;
      const lines = cartLines(cart);
      const fingerprint = JSON.stringify([cart.currency, cart.total_price, lines]);
      if (lastCart !== fingerprint) {
        event('cart_snapshot', {items:lines, currency:cart.currency, total:cart.total_price / 100});
        lastCart = fingerprint;
      }
    } catch {} finally { pending = null; }
    })();
    return pending;
  }
  function scheduleSnapshot() { clearTimeout(timer); timer = setTimeout(snapshot, 300); }
  const mutation = input => {
    try {
      const url = new URL(input instanceof Request ? input.url : String(input), location.origin);
      return url.origin === location.origin && /\/cart\/(add|change|update|clear)(\.js)?$/.test(url.pathname);
    } catch { return false; }
  };
  window.fetch = async function (...args) {
    const response = await nativeFetch(...args);
    if (response.ok && mutation(args[0])) scheduleSnapshot();
    return response;
  };
  const originalOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (...args) {
    if (mutation(args[1])) this.addEventListener('load', () => {
      if (this.status >= 200 && this.status < 300) scheduleSnapshot();
    }, {once:true});
    return originalOpen.apply(this, args);
  };
  function start() {
    if (!allowed()) {
      queue.length = 0; persist(); lastCart = null; started = false; visitor = session = null;
      if (writer) writer.revoke().catch(()=>{});
      writer = null; writerPromise = null;
      try { localStorage.removeItem('toutera_visitor'); sessionStorage.removeItem('toutera_session'); } catch {}
      return;
    }
    if (started) return;
    started = true;
    identities(); restore();
    // Warm Auth immediately, rather than waiting five seconds or for unload.
    void loadWriter().catch(()=>{});
    let referrer = '';
    try { referrer = document.referrer ? new URL(document.referrer).hostname : ''; } catch {}
    event('page_viewed', {referrer, device:matchMedia('(max-width: 768px)').matches ? 'mobile' : 'desktop'});
    if (config.product) event('product_viewed', {product_id:String(config.product.id), title:config.product.title, handle:config.product.handle});
    snapshot();
  }
  document.addEventListener('visitorConsentCollected', start);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush(); else snapshot();
  });
  window.addEventListener('pagehide', flush);
  const checkoutForms = new WeakSet();
  const checkoutLinks = new WeakSet();
  const checkoutUrl = value => {
    try { const url=new URL(value,location.origin); return url.origin===location.origin && /^\/checkouts?(?:[/?]|$)/.test(url.pathname); }
    catch { return false; }
  };
  async function saveBeforeCheckout() {
    // Cart snapshot and checkout event are already durable before any navigation.
    // Never hold checkout longer than 1.5s if Firebase/network is unavailable.
    await Promise.race([(async()=>{await snapshot();await flush();})(),new Promise(resolve=>setTimeout(resolve,1500))]);
  }
  document.addEventListener('click', e => {
    const target = e.target instanceof Element ? e.target.closest('a,button,input') : null;
    if (!target || !allowed()) return;
    if (target instanceof HTMLAnchorElement && checkoutUrl(target.href)) {
      if(e.defaultPrevented || e.button>0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || target.target==='_blank') return;
      e.preventDefault();
      if(checkoutLinks.has(target))return;
      checkoutLinks.add(target);
      event('checkout_clicked');
      void saveBeforeCheckout().finally(()=>location.assign(target.href));
    }
    if (target instanceof HTMLAnchorElement && /(?:wa\.me|api\.whatsapp\.com)/.test(target.hostname)) {
      event('whatsapp_clicked');
    }
  });
  document.addEventListener('submit', e => {
    const form=e.target, submitter=e.submitter;
    if(checkoutForms.has(form)) {checkoutForms.delete(form);return;}
    if (!allowed() || e.defaultPrevented || !form?.action || !/\/cart(?:[/?]|$)/.test(form.action) || submitter?.name!=='checkout') return;
    e.preventDefault();
    event('checkout_clicked');
    void saveBeforeCheckout().finally(()=>{
      checkoutForms.add(form);
      form.requestSubmit(submitter);
    });
  });
  window.addEventListener('online',flush);
  setInterval(flush, 5000);
  setInterval(() => { if (document.visibilityState === 'visible') snapshot(); }, 30000);
  if (window.Shopify?.customerPrivacy) start();
  else window.Shopify?.loadFeatures?.([{name:'consent-tracking-api',version:'0.1'}], error => { if (!error) start(); });
})();
